import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

/**
 * ExtraHeader — Common header for all ExtraSection pages.
 * Shows:
 *   • Left  : ← back arrow + page title
 *   • Right : Home icon → navigates to "/"
 *
 * Props:
 *   title    (string)   — page title shown in the bar
 *   onBack   (function) — optional custom back handler; defaults to navigate(-1)
 */
export default function ExtraHeader({ title = "", onBack }) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleHome = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#002B5B",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, sm: 3 },
        py: { xs: 1.2, sm: 1.5 },
        boxSizing: "border-box",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 2px 8px rgba(0,43,91,0.25)",
      }}
    >
      {/* Left: Back arrow + Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          "&:hover .back-text": { color: "#B3D9F7" },
          "&:hover .back-icon": { color: "#B3D9F7" },
        }}
        onClick={handleBack}
      >
        <ArrowBackIcon
          className="back-icon"
          sx={{
            color: "#fff",
            fontSize: { xs: "1.1rem", sm: "1.3rem" },
            transition: "color 0.2s ease",
          }}
        />
        <Typography
          className="back-text"
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "0.95rem", sm: "1.1rem" },
            letterSpacing: "0.3px",
            transition: "color 0.2s ease",
            userSelect: "none",
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Right: Home Icon */}
      <Tooltip title="Go to Home" placement="bottom">
        <IconButton
          onClick={handleHome}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            border: "1.5px solid rgba(255,255,255,0.25)",
            borderRadius: 2,
            p: { xs: 0.6, sm: 0.8 },
            "&:hover": {
              backgroundColor: "#0288D1",
              borderColor: "#0288D1",
              transform: "scale(1.08)",
            },
            transition: "all 0.25s ease",
          }}
        >
          <HomeIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}